import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Spinner, Alert, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { fmtRelative, fmtDate } from '../lib/lookups';
import { SOURCE_TYPES, PARSING_METHODS, DS_STATUSES, sourceTypeLabel, dsStatusMeta } from '../lib/dataSourceMeta';

export default function DataManagementPage() {
  const { stateId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, sum] = await Promise.all([
          adminApi.get(`/admin/states/${stateId}`),
          adminApi.get(`/admin/states/${stateId}/data-management/summary`),
        ]);
        if (!active) return;
        setState(s.data);
        setSummary(sum.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [stateId]);

  return (
    <>
      <PageHeader
        title="Data Management"
        subtitle={state ? `${state.name} (${state.abbreviation}) · Source URLs and import config for civic data` : ''}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {' / '}{state && <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state.name}</Link>}
            {' / '}Data Management
          </span>
        }
        actions={
          <div className="d-flex gap-2">
            <Link to={`/admin/states/${stateId}/import-jobs`}><Button variant="outline-secondary" data-testid="dm-view-jobs-button"><i className="bi bi-clock-history me-1" /> Import jobs</Button></Link>
            <Link to={`/admin/states/${stateId}/data-sources`}><Button variant="outline-secondary" data-testid="dm-view-all-button"><i className="bi bi-list-ul me-1" /> All sources</Button></Link>
            <Link to={`/admin/states/${stateId}/data-sources/new`}><Button variant="primary" data-testid="dm-new-source-button"><i className="bi bi-plus-lg me-1" /> New data source</Button></Link>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center p-5"><Spinner animation="border" /></div>
        ) : summary ? (
          <>
            <Alert variant="info" className="d-flex gap-2 align-items-start">
              <i className="bi bi-info-circle pt-1" />
              <div>
                <strong>Scraping is not yet active.</strong> Add saved source URLs here so a future import/scraper service can read them.
                Scraper hooks <code>POST /api/admin/states/{stateId}/data-sources/&lt;id&gt;/mark-imported</code> and <code>/mark-failed</code> are ready for integration.
              </div>
            </Alert>

            <Row className="g-3 mb-4">
              <Col xs={6} md={3} xl={2}>
                <Card className="kpi-card kpi-total" data-testid="dm-kpi-total">
                  <Card.Body>
                    <div className="kpi-label">Total Sources</div>
                    <div className="kpi-value">{summary.total}</div>
                  </Card.Body>
                </Card>
              </Col>
              {DS_STATUSES.map((s) => (
                <Col xs={6} md={3} xl={2} key={s.value}>
                  <Card className={`kpi-card ${s.value === 'active' ? 'kpi-published' : s.value === 'paused' ? 'kpi-draft' : 'kpi-review'}`}
                        data-testid={`dm-kpi-${s.value}`}>
                    <Card.Body>
                      <div className="kpi-label">{s.label}</div>
                      <div className="kpi-value">{summary.by_status?.[s.value] || 0}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row className="g-3 mb-4">
              <Col xs={12} lg={6}>
                <Card>
                  <Card.Header>Sources by type</Card.Header>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {SOURCE_TYPES.map((t) => (
                        <tr key={t.value} data-testid={`dm-type-row-${t.value}`}>
                          <td><i className={`bi ${t.icon} me-2 text-muted`} />{t.label}</td>
                          <td className="text-end">
                            <Link to={`/admin/states/${stateId}/data-sources?source_type=${t.value}`} className="text-decoration-none">
                              {summary.by_source_type?.[t.value] || 0} <i className="bi bi-arrow-right" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </Col>
              <Col xs={12} lg={6}>
                <Card>
                  <Card.Header>Sources by parsing method</Card.Header>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      {PARSING_METHODS.map((m) => (
                        <tr key={m.value}>
                          <td>{m.label}</td>
                          <td className="text-end">{summary.by_parsing_method?.[m.value] || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              </Col>
            </Row>

            <Row className="g-3">
              <Col xs={12} lg={6}>
                <Card data-testid="dm-recent-failures">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span><i className="bi bi-exclamation-triangle me-2 text-warning" />Recent failures</span>
                    <Link to={`/admin/states/${stateId}/data-sources?status=failed`} style={{ fontSize: '0.85rem' }}>View all</Link>
                  </Card.Header>
                  {summary.recent_failures.length === 0 ? (
                    <div className="empty-state"><i className="bi bi-check-circle" /><div>No failed sources — nice work.</div></div>
                  ) : (
                    <Table size="sm" hover responsive className="mb-0">
                      <tbody>
                        {summary.recent_failures.map((s) => (
                          <tr key={s.id} onClick={() => navigate(`/admin/states/${stateId}/data-sources/${s.id}`)} style={{ cursor: 'pointer' }}>
                            <td>
                              <div className="fw-semibold">{s.name}</div>
                              <div className="text-muted" style={{ fontSize: '0.78rem' }}>{sourceTypeLabel(s.source_type)} · {fmtRelative(s.updated_at)}</div>
                              {s.last_error && <div className="text-danger" style={{ fontSize: '0.78rem' }}>{s.last_error}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>
              </Col>
              <Col xs={12} lg={6}>
                <Card data-testid="dm-recent-imports">
                  <Card.Header><i className="bi bi-clock-history me-2 text-success" />Recent imports</Card.Header>
                  {summary.recent_imports.length === 0 ? (
                    <div className="empty-state"><i className="bi bi-inbox" /><div>No imports recorded yet.</div></div>
                  ) : (
                    <Table size="sm" hover responsive className="mb-0">
                      <tbody>
                        {summary.recent_imports.map((s) => (
                          <tr key={s.id} onClick={() => navigate(`/admin/states/${stateId}/data-sources/${s.id}`)} style={{ cursor: 'pointer' }}>
                            <td>
                              <div className="fw-semibold">{s.name}</div>
                              <div className="text-muted" style={{ fontSize: '0.78rem' }}>{sourceTypeLabel(s.source_type)} · imported {fmtDate(s.last_imported_at)}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
      </div>
    </>
  );
}
